import {
  isSameObstruction,
  NO_OBSTRUCTION,
  type SupportLauncherObstruction,
} from "@/lib/support-launcher-placement";
import { useEffect, useId, useSyncExternalStore } from "react";

/**
 * Module-level registry of what occupies the corner the native Intercom
 * launcher floats in.
 *
 * The launcher renders at `z-index: 2147483000`, above every surface the app
 * can draw, so it can only be kept clear of them by being moved out of their
 * way. Each floating surface publishes here the footprint it keeps while it is
 * mounted, and the launcher rides above the tallest one — back in its resting
 * corner as soon as none is left. A surface that also publishes a centre asks
 * the launcher to perch on it, lined up with it, rather than only to clear it.
 *
 * Read on desktop only — on a phone the bubble isn't shown at all (support
 * lives in the account menu there), so nothing published here can reach it.
 * Surfaces register all the same: they know their own footprint, not which
 * format is reading it.
 *
 * Kept outside React (same pattern as `companyPreviewPanelStore`) so unrelated
 * surfaces can register without knowing about the launcher, or about each
 * other.
 */
const obstructions = new Map<string, SupportLauncherObstruction>();
const obstructionListeners = new Set<() => void>();

/** Cached so `getSnapshot` returns a stable value between actual changes. */
let tallestObstruction: SupportLauncherObstruction = NO_OBSTRUCTION;

function recompute(): void {
  let next = NO_OBSTRUCTION;
  obstructions.forEach((obstruction) => {
    if (obstruction.bottom > next.bottom) next = obstruction;
  });
  if (isSameObstruction(next, tallestObstruction)) return;
  tallestObstruction = next;
  obstructionListeners.forEach((listener) => listener());
}

/**
 * Declares the footprint `key` keeps in the launcher's corner. Re-registering
 * the same key replaces its previous one.
 */
function registerObstruction(
  key: string,
  obstruction: SupportLauncherObstruction,
): void {
  const registered = obstructions.get(key);
  if (registered && isSameObstruction(registered, obstruction)) return;
  obstructions.set(key, obstruction);
  recompute();
}

/** Drops `key`'s footprint — called when the surface unmounts. */
function clearObstruction(key: string): void {
  if (!obstructions.delete(key)) return;
  recompute();
}

function subscribeObstruction(listener: () => void): () => void {
  obstructionListeners.add(listener);
  return () => {
    obstructionListeners.delete(listener);
  };
}

function getObstructionSnapshot(): SupportLauncherObstruction {
  return tallestObstruction;
}

/** The registered obstruction the launcher has to sit above, if any. */
export function useTallestObstruction(): SupportLauncherObstruction {
  return useSyncExternalStore(subscribeObstruction, getObstructionSnapshot);
}

/**
 * Publishes, for as long as the calling component is mounted, that the caller
 * covers the bottom `bottomPx` pixels of the viewport — so the support launcher
 * sits above it instead of on top of it.
 *
 * `centreRightPx`, the inset of the caller's centre from the viewport's right
 * edge, additionally asks the launcher to perch on it: lined up with it, rather
 * than merely clear of it. Left `null`, the launcher only rises.
 *
 * Taken as two numbers rather than one object so a caller re-measuring on every
 * frame of a slide can hold them in state without a fresh object per frame.
 */
export function useSupportLauncherObstruction(
  bottomPx: number,
  centreRightPx: number | null = null,
): void {
  const key = useId();

  useEffect(() => {
    registerObstruction(key, { bottom: bottomPx, centreRight: centreRightPx });
  }, [key, bottomPx, centreRightPx]);

  // Kept apart from the registration above so a re-measure — one per frame, as
  // a resizable panel is dragged — doesn't churn the store through a cleared
  // state.
  useEffect(() => () => clearObstruction(key), [key]);
}
